'use client';

import { useState } from 'react';

import { usePlans } from '../hooks/usePlans';
import type { Interval, Plan } from '../types/billing.types';
import { PlanCard } from './PlanCard';

/**
 * Plan comparison — the canonical place users see what each tier costs.
 * Reads `/billing/plans` so prices, limits, and provider availability all
 * track real config. When Stripe is disabled the checkout buttons render
 * in a disabled "Payments coming soon" state.
 */
export function PricingTable() {
  const { data, isLoading, isError } = usePlans();
  const [interval, setInterval] = useState<Interval>('monthly');

  if (isLoading) {
    return (
      <div className="text-sm text-muted-foreground">Loading plans…</div>
    );
  }
  if (isError || !data) {
    return (
      <div className="rounded-lg border border-border p-4 text-sm text-muted-foreground">
        Couldn&apos;t load plans. Please try again.
      </div>
    );
  }

  // Sort plans by tier order so the cards always read free → starter → pro → elite.
  const orderedPlans: Plan[] = ['starter', 'pro', 'elite']
    .map((tier) => data.plans.find((p) => p.tier === tier))
    .filter((p): p is Plan => Boolean(p));

  return (
    <div className="space-y-4">
      <IntervalToggle value={interval} onChange={setInterval} />
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {orderedPlans.map((plan) => (
          <PlanCard
            key={plan.tier}
            plan={plan}
            interval={interval}
            stripeEnabled={data.stripe_enabled}
            cryptoEnabled={data.crypto_enabled}
          />
        ))}
      </div>
      {!data.stripe_enabled && !data.crypto_enabled && (
        <p className="text-center text-xs text-muted-foreground">
          Payments aren&apos;t enabled in this environment yet.
        </p>
      )}
    </div>
  );
}

function IntervalToggle({
  value,
  onChange,
}: {
  value: Interval;
  onChange: (interval: Interval) => void;
}) {
  return (
    <div className="mx-auto flex w-fit rounded-full border border-border bg-background p-1 text-xs">
      {(['monthly', 'annual'] as Interval[]).map((opt) => {
        const active = opt === value;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={`rounded-full px-4 py-1 transition ${
              active
                ? 'bg-foreground text-background'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {opt === 'monthly' ? 'Monthly' : 'Annual · 2 months free'}
          </button>
        );
      })}
    </div>
  );
}
